import { useEffect, useState } from 'react'
import { InputTextarea } from 'primereact/inputtextarea'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormField, { FormGrid } from '../../../components/ui/FormField'
import FileUpload from '../../../components/ui/FileUpload'
import { useToast } from '../../../components/ui/ToastProvider'
import { DOCUMENT_ACCEPT } from '../../../lib/uploadAccept'

/** Onaya gönderme notunun ürün sınırı — yeniden açma notuyla **aynı**. */
export const COMPLETION_NOTE_MAX_LENGTH = 2000

interface Props {
  visible: boolean
  submitting?: boolean
  /**
   * Dosya eki alanı çizilsin mi — **varsayılan `false` (fail-closed)**.
   *
   * <p>Statü değiştirmek ile göreve dosya yüklemek **ayrı yetkilerdir**: uçlar
   * {@code HAS_ACTION_STATUS_CHANGE} ve {@code HAS_ACTION_MANAGE} ile korunur. Kapı olmasaydı,
   * statüyü değiştirebilen ama dosya yükleyemeyen bir kullanıcı (aynı sayfada Dokümanlar bölümü
   * salt okunurken) burada dosya seçebilir, statü **geri alınamaz biçimde** yazıldıktan sonra
   * yükleme {@code 403} alır ve seçtiği dosyalar kaybolurdu.
   *
   * <p>Çağıran, Dokümanlar bölümüne verdiği <b>aynı</b> ölçütü geçmelidir; iki yüzey ayrışırsa
   * kullanıcı bir yerde yükleyip öbüründe yükleyemez görünür. (Aynı savunma alt görev not
   * yüzeyinde de var — bkz. `SubActionDetailPage` `notesAddable` yorumu: "ekran bunu sormazsa
   * uçtan GENİŞ olur".)
   */
  filesEnabled?: boolean
  /**
   * Onayda çağrılır; kırpılmış not gelir ve not **zorunlu** olduğu için **her zaman doludur**
   * (29.09.2026). Boş dize üretilmez — boş bırakılan pop-up gönderilemez.
   *
   * `files` kullanıcının bu pop-up'ta seçtiği **yeni** dosyalardır (0..n) ve statü değişikliği
   * **başarılı olduktan sonra** yüklenmelidir: statü yazılamazsa görev eskisi gibi kalmalı, ona
   * bağlanacak dosya da yüklenmemelidir.
   */
  onSubmit: (completionNote: string, files: File[]) => void
  onHide: () => void
}

/**
 * <b>Onaya gönderme notu</b> pop-up'ı (105) — görev <b>331</b>'e çekilirken açılır.
 *
 * <p><b>331'in adı 29.09.2026'da REFERANS'ta "Tamamlandı" → "Onay Bekleniyor" olarak değiştirildi</b>
 * (analist). Kod tarafındaki {@code COMPLETED} sabiti, {@code COMPLETION_NOTE} kolonu ve bu
 * bileşenin dosya adı <b>tarihsel</b>dir ve bilinçli olarak korunur — DB kolonu yeniden
 * adlandırılmaz, sabit adını değiştirmek de akışa değmeyen geniş bir refactor olurdu. Kullanıcıya
 * görünen her metin i18n'den gelir ve <b>"Onaya Gönderme Notu"</b> der.
 *
 * <p><b>Not ZORUNLUDUR</b> (kullanıcı kararı 29.09.2026 — <b>önceki kural tersineydi</b>: 2026-09-16'da
 * isteğe bağlı kararlaştırılmıştı, 105/FR-025b). Boş ya da yalnız boşluk içeren not gönderilemez:
 * etikette {@code *} vardır, gönder düğmesi pasiftir ve alana <b>dokunulduktan sonra</b> gerekçe
 * yazar (açılışta değil — kullanılmamış bir formu kırmızıyla açmak hatayı gürültüye çevirir). <b>Vazgeçildiğinde</b> (başlıktaki ×,
 * maske tıklaması ya da Esc — paylaşılan kabukta ayrı bir İptal butonu yoktur) statü değişmez,
 * istek atılmaz.
 *
 * <p>Notun gittiği yer görevin <b>kendi alanıdır</b> ({@code COMPLETION_NOTE}), görev notları
 * ({@code MINUTES_NOTE}) değil: Toplantı Yönetimi ekranındaki "Onaya Gönderme Notu" kolonu bu alanı
 * gösterir ve tek değerlidir (görev yeniden gönderilince üzerine yazılır).
 *
 * <p><b>Boş dize artık gönderilemez</b> — not zorunlu olduğu için "boş not eskisini siler" yolu
 * (FR-025b) kullanıcı arayüzünden <b>erişilemez</b> hâle geldi. Sunucu sözleşmesi değişmedi:
 * {@code null} hâlâ "alan gönderilmedi → eskisi korunur", boş dize hâlâ "temizle" demektir; bu
 * bileşen bugün ikisini de üretmez, her zaman dolu bir metin gönderir.
 *
 * <p><b>Dosya eki</b> (30.09.2026): pop-up'tan <b>birden çok</b> dosya seçilebilir; ek <b>isteğe
 * bağlıdır</b> (zorunlu olan yalnız nottur) ve alan yalnız <b>dosya yükleme yetkisi olan</b>
 * kullanıcıya çizilir ({@code filesEnabled}, fail-closed — gerekçe prop'un javadoc'unda). <b>Daha önce eklenmiş dosyalar listelenmez</b> — bu
 * pop-up görevin doküman arşivini yönetmez. Dosyalar statü değişikliği <b>başarılı olduktan sonra</b>
 * yüklenir ve görevin kendi doküman havuzuna ({@code ATTACHED_FILE}, {@code MEETING_MINUTES}) yazılır;
 * Toplantı Yönetimi satırındaki ataç işareti de o havuza bakar.
 *
 * <p><b>`maxLength` KULLANILMAZ</b> (FR-047a): sınırın üzerine yazmayı engelleyen bir kısıt fazlasını
 * <b>sessizce keser</b> ve uyarı metni hiçbir zaman görünmezdi — iki gereksinim birbirini iptal
 * ederdi. Alan yazmaya izin verir, sayaç kalanı gösterir, gönderim engellenir ve uyarı çıkar.
 */
export default function CompletionNoteDialog({
  visible,
  submitting,
  filesEnabled = false,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()
  const [note, setNote] = useState('')
  /**
   * Bu gönderimde eklenecek dosyalar (30.09.2026 kullanıcı isteği) — **birden çok olabilir**.
   *
   * <p><b>Daha önce eklenmiş dosyalar burada GÖSTERİLMEZ</b> (kullanıcı kuralı): pop-up "onaya
   * gönderirken ne ekliyorum" sorusunu sorar, görevin doküman arşivini yönetmez — o iş Görev
   * Detay'daki Dokümanlar bölümünündür ve listeyi burada da çizmek, hangi dosyanın bu gönderime ait
   * olduğunu belirsizleştirirdi. Dosya **isteğe bağlıdır**; zorunlu olan yalnız nottur.
   */
  const [files, setFiles] = useState<File[]>([])
  /**
   * Kullanıcı alana dokundu mu? Zorunluluk hatası **açılışta gösterilmez**: pop-up daha hiç
   * kullanılmadan kırmızı bir uyarıyla açılmak, uygulamadaki diğer formların tersidir ve hatayı
   * gürültüye çevirir. Zorunluluğu açılışta anlatan şey etiketteki `*` ve pasif gönder düğmesidir;
   * metin, kullanıcı yazıp sildiğinde belirir.
   */
  const [touched, setTouched] = useState(false)

  // Her açılışta temiz form: önceki denemenin metni kalırsa kullanıcı onu yanlışlıkla gönderir.
  useEffect(() => {
    if (visible) {
      setNote('')
      setFiles([])
      setTouched(false)
    }
  }, [visible])

  const length = note.trim().length
  const tooLong = length > COMPLETION_NOTE_MAX_LENGTH
  // Zorunluluk (29.09.2026): yalnız boşluk da boş sayılır — `trim()` sonrası uzunluğa bakılır.
  const missing = length === 0

  const submit = () => {
    if (submitting) return
    if (missing) {
      // `submitDisabled` bu dalı UI'dan erişilemez kılar; iç kapı yine de durur (FR-046 deseni).
      toast.error(t('actions.detail.completionNote.required'))
      return
    }
    if (tooLong) {
      // Doğrulama başarısızsa hiçbir sunucu çağrısı yapılmaz (FR-046).
      // NOT: bu dal şu an UI'dan ERİŞİLEMEZ — `submitDisabled={tooLong}` hem görünür butonu hem
      // `FormDialog`un Enter-submit yolunu kapatıyor (ikisi de `submitDisabled`e bakıyor). Bilinçle
      // duruyor: FR-046 açık bir sözleşme ve `submitDisabled` ifadesi ileride gevşetilirse iç kapı
      // hâlâ durdurur. Asıl kullanıcı bildirimi burada değil, alanın `aria-live` hata bölgesindedir.
      // Anahtar KENDİ ağacımızdan okunur: bu pop-up Görev Detay ekranına aittir ve metnini başka bir
      // feature'ın (`meetingManagement.*`) alt ağacından almak, o blok yeniden adlandırıldığında
      // BURADA ham anahtar gösterirdi.
      toast.error(
        t('actions.detail.completionNote.tooLong', { max: COMPLETION_NOTE_MAX_LENGTH }),
      )
      return
    }
    // Buraya yalnız dolu not ulaşır (yukarıdaki `missing` kapısı). Sunucu `null`'ı "alan hiç
    // gönderilmedi" sayıp eskisini korur; bu bileşen artık ne `null` ne boş dize üretir.
    onSubmit(note.trim(), files)
  }

  return (
    <FormDialog
      visible={visible}
      header={t('actions.detail.completionNote.title')}
      submitLabel={t('actions.detail.completionNote.submit')}
      submitting={submitting}
      submitDisabled={missing || tooLong}
      onSubmit={submit}
      onHide={onHide}
    >
      <FormGrid>
        <FormField
          label={t('actions.detail.completionNote.label')}
          htmlFor="completion-note"
          // Hata metni paylaşılan alandan gelir; kutu ELLE kurulmaz (Reuse Mandate). `FormField`
          // onu her zaman DOM'da duran bir `aria-live` bölgesinde çizer ve `id`'sini `htmlFor`dan
          // türetir — `aria-describedby` aşağıda o id'yi gösteriyor.
          required
          // Sıra önemli: boşken "zorunludur" (yalnız alana dokunulduysa), doluyken ve sınır
          // aşıldığında "çok uzun".
          error={
            missing && touched
              ? t('actions.detail.completionNote.required')
              : tooLong
                ? t('actions.detail.completionNote.tooLong', { max: COMPLETION_NOTE_MAX_LENGTH })
                : undefined
          }
          full
        >
          <InputTextarea
            id="completion-note"
            value={note}
            onChange={(e) => {
              setTouched(true)
              setNote(e.target.value)
            }}
            onBlur={() => setTouched(true)}
            rows={4}
            autoResize
            // Sınır aşıldığında durum GÖRSEL OLMAYAN yolla da bildirilmeli (THY inceleme bulgusu,
            // 16.09.2026): eskiden yalnız sayacın sınıfı kırmızıya dönüyordu. Üstelik `submitDisabled`
            // yüzünden Kaydet'e basmak mümkün olmadığı için `submit()` içindeki uyarı hiç görünmüyordu —
            // ekran okuyucu kullanıcısına ulaşan TEK bilgi yoktu, buton sessizce pasifti.
            // Zorunluluk AÇILIŞTA da duyurulur ama HATA olarak değil: `aria-required` alanın
            // zorunlu olduğunu söyler, `aria-invalid` ise ancak kullanıcı dokunduktan (ya da sınırı
            // aştıktan) sonra "bu hâliyle geçersiz" der. İkisini birleştirmek, formu daha
            // kullanılmadan hatalı ilan ederdi.
            aria-required
            aria-invalid={(missing && touched) || tooLong}
            aria-describedby="completion-note-counter completion-note-err"
          />
          {/* Sayaç bilinçle canlı DEĞİL: her tuşta değiştiği için `aria-live` vermek kullanıcıyı
              sürekli böler. Duyurulan şey sayaç değil, eşiğin aşılmış olmasıdır (FormField). */}
          <small
            id="completion-note-counter"
            className={`mm-note-counter${tooLong ? ' mm-over' : ''}`}
          >
            {length} / {COMPLETION_NOTE_MAX_LENGTH}
          </small>
        </FormField>

        {/*
          DOSYA EKİ (30.09.2026) — isteğe bağlı, çoklu. Zorunluluk işareti YOKTUR: zorunlu olan yalnız
          nottur, dosya eklemeden de onaya gönderilebilir.

          Kabul listesi Görev Detay'daki Dokümanlar bölümüyle AYNI (`DOCUMENT_ACCEPT`): dosyalar aynı
          havuza yazılıyor, iki yüzeyin farklı uzantı kabul etmesi kullanıcıya sebebi görünmeyen bir
          ayrım olurdu.

          Seçilen dosyaların listesi GÖSTERİLİR (`showList` varsayılan): burada dosya, "kaydet"e kadar
          bekleyen YEREL bir seçimdir (Prensip Kararları'nın tersine, orada seçilen anında sunucuya
          gider) — kullanıcının ne göndereceğini görmesi ve yanlış seçtiğini kaldırabilmesi gerekir.

          `FormField` KULLANILMAZ: onun etiketi bir `<label htmlFor>`dur ve `FileUpload` id taşıyan bir
          kontrol render etmez (gizli input + dropzone düğmesi) — hiçbir şeye bağlanmayan bir label
          erişilebilirlik açığı olurdu. Metin bileşenin KENDİ `label` prop'uyla dropzone düğmesinin
          üstüne yazılır; sarmalayıcı yalnız `ui-field full` ile ızgaranın iki kolonunu kaplar.
        */}
        {filesEnabled && (
          <div className="ui-field full">
            <FileUpload
              files={files}
              onChange={setFiles}
              accept={DOCUMENT_ACCEPT}
              label={t('actions.detail.completionNote.files')}
              hint={t('actions.detail.completionNote.filesHint')}
            />
          </div>
        )}
      </FormGrid>
    </FormDialog>
  )
}
